import React, { useState, useLayoutEffect, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Rocket, MousePointer } from 'lucide-react';

export interface TourStep {
  /** CSS selector or data-tour attribute value to target */
  target: string;
  /** Title of this step */
  title: string;
  /** Description/instruction for this step */
  content: string;
  /** Position of tooltip relative to target */
  position?: 'top' | 'bottom' | 'left' | 'right';
  /** If true, user must click the target to proceed (interactive mode) */
  waitForClick?: boolean;
  /** Optional action text displayed when waitForClick is true */
  clickText?: string;
  /** If true, don't show the dark overlay - allows interacting with all elements */
  noOverlay?: boolean;
  /** If true, allow full interaction with the highlighted element (dropdowns, inputs, etc.) */
  allowInteraction?: boolean;
  /** If true, skip finding target - shows centered tooltip with full blocking overlay */
  skipTarget?: boolean;
  /** If true, skip this step quickly if the target element is not found (useful for optional modals) */
  skipIfNotFound?: boolean;
}

interface GuidedTourProps {
  /** Tour steps configuration */
  steps: TourStep[];
  /** Storage key to persist completion */
  storageKey: string;
  /** Callback when tour is completed */
  onComplete?: () => void;
  /** Callback when tour is skipped */
  onSkip?: () => void;
  /** Optional: force show even if completed before */
  forceShow?: boolean;
  /** Optional: callback to check if current step can advance (for non-waitForClick steps) */
  canAdvance?: (stepIndex: number) => boolean;
  /** Optional: callback when step changes */
  onStepChange?: (fromStep: number, toStep: number) => void;
  /** Optional: controlled step index - allows parent to control which step is shown */
  controlledStep?: number;
  /** Optional: callback when step would change - allows parent to handle step changes */
  onRequestStepChange?: (fromStep: number, toStep: number) => void;
}

export const GuidedTour: React.FC<GuidedTourProps> = ({
  steps,
  // storageKey is kept for backwards compatibility but is deprecated
  // Tour completion is now controlled by parent via forceShow prop
  storageKey: _storageKey,
  onComplete,
  onSkip,
  forceShow = false,
  canAdvance,
  onStepChange,
  controlledStep,
  onRequestStepChange,
}) => {
  // Use forceShow to determine if tour is active
  // We no longer use localStorage for tour completion - controlled by parent via forceShow
  const [isActive, setIsActive] = useState(forceShow);
  // Track if tour was explicitly completed/skipped - prevents reactivation while Firebase updates
  const [wasCompleted, setWasCompleted] = useState(false);

  // Sync isActive with forceShow prop changes (e.g., when forceShow changes from false to true after loading)
  useEffect(() => {
    // Only activate if forceShow is true, tour is not active, AND was not explicitly completed
    // This prevents the tour from reappearing while waiting for Firebase to update
    if (forceShow && !isActive && !wasCompleted) {
      // Scroll to top when tour first activates to ensure proper highlight positioning
      // This fixes issues when navigating to the page with the page scrolled down
      window.scrollTo({ top: 0, behavior: 'instant' });
      // Small delay to let the scroll complete before activating the tour
      setTimeout(() => {
        setIsActive(true);
      }, 100);
    }
    // Reset wasCompleted flag when forceShow becomes false (Firebase confirmed tour is done)
    if (!forceShow && wasCompleted) {
      setWasCompleted(false);
    }
  }, [forceShow, isActive, wasCompleted]);

  // Support controlled or uncontrolled step management
  const [internalStep, setInternalStep] = useState(0);
  const isControlled = controlledStep !== undefined;
  const currentStep = isControlled ? controlledStep : internalStep;

  // Wrapper to update step - handles both controlled and uncontrolled modes
  const setCurrentStep = (step: number | ((prev: number) => number)) => {
    if (isControlled) {
      // In controlled mode, we don't update internal state
      // Parent handles step changes via onRequestStepChange
      return;
    }
    setInternalStep(step);
  };
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [targetElement, setTargetElement] = useState<Element | null>(null);
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  // Store the bounds of the scrollable container to clip the highlight
  const [containerBounds, setContainerBounds] = useState<DOMRect | null>(null);

  const step = steps[currentStep];

  // Use refs to always have access to the latest callback functions in intervals/timeouts
  // This prevents stale closure issues when canAdvance depends on state that changes
  // We update refs directly on every render instead of using useEffect - this is simpler
  // and ensures the refs are always up-to-date before any callbacks that might read them
  const canAdvanceRef = useRef(canAdvance);
  const currentStepRef = useRef(currentStep);
  const stepRef = useRef(step);
  const onRequestStepChangeRef = useRef(onRequestStepChange);
  const onStepChangeRef = useRef(onStepChange);

  // Update refs on every render (synchronously, before any callbacks run)
  canAdvanceRef.current = canAdvance;
  currentStepRef.current = currentStep;
  stepRef.current = step;
  onRequestStepChangeRef.current = onRequestStepChange;
  onStepChangeRef.current = onStepChange;

  // Find the target element and get its position
  // Uses polling to wait for elements that may appear later (e.g., inside modals)
  // Also watches for element removal to auto-advance when appropriate
  useLayoutEffect(() => {
    if (!isActive || !step) return;

    // If skipTarget is true, don't try to find a target - just show centered tooltip with full overlay
    if (step.skipTarget) {
      setTargetRect(null);
      setTargetElement(null);
      return;
    }

    let attempts = 0;
    const maxAttempts = 60; // Try for up to 6 seconds (60 * 100ms) to allow for async operations
    let intervalId: NodeJS.Timeout;
    let watchIntervalId: NodeJS.Timeout;
    let cleanupFn: (() => void) | undefined;
    let foundElement: Element | null = null;

    const findTarget = () => {
      // Try data-tour attribute first
      let element = document.querySelector(`[data-tour="${step.target}"]`);
      // Fallback to CSS selector
      if (!element) {
        element = document.querySelector(step.target);
      }

      // If skipIfNotFound is set and element not found, skip immediately after first check
      if (!element && step.skipIfNotFound) {
        // On first check, skip immediately - don't wait
        if (attempts === 0) {
          clearInterval(intervalId);
          setTargetRect(null);
          setTargetElement(null);
          // Auto-advance to next step since target is optional and not found
          advanceToNextStep();
          return;
        }
      }

      if (element) {
        clearInterval(intervalId);
        foundElement = element;
        const rect = element.getBoundingClientRect();
        setTargetRect(rect);
        setTargetElement(element);

        // Scroll element into view if needed, accounting for fixed header
        // The header container includes all status banners, admin toolbar, and main header
        // getBoundingClientRect().height gives the actual rendered height at runtime
        const headerElement = document.querySelector('[data-header-container]');
        const headerHeight = headerElement ? headerElement.getBoundingClientRect().height : 0;
        const headerOffset = headerHeight + 20; // Add some padding below the header
        const viewportHeight = window.innerHeight;

        // Check if element is already visible in the viewport (below header and above bottom)
        const isElementVisible = rect.top >= headerOffset && rect.bottom <= viewportHeight - 20;

        // Only scroll if element is not already visible
        if (!isElementVisible) {
          // Calculate the target scroll position to center the element below the header
          const elementTop = rect.top + window.scrollY;
          const elementHeight = rect.height;
          const availableHeight = viewportHeight - headerOffset;

          // Calculate scroll position to center the element in the available viewport area (below header)
          const targetScrollY = elementTop - headerOffset - (availableHeight - elementHeight) / 2;

          // Smooth scroll to the calculated position
          window.scrollTo({
            top: Math.max(0, targetScrollY),
            behavior: 'smooth',
          });
        }

        // Store click handler reference for proper cleanup
        let handleTargetClick: (() => void) | null = null;

        // If this step requires click, add click listener
        if (step.waitForClick) {
          handleTargetClick = () => {
            // Small delay to let the original click handler run first
            setTimeout(() => {
              advanceToNextStep();
            }, 100);
          };
          element.addEventListener('click', handleTargetClick, { once: true });
        }

        // Find the closest scrollable container for clipping
        let closestScrollableContainer: Element | null = null;
        let tempParent = element.parentElement;
        while (tempParent) {
          const style = window.getComputedStyle(tempParent);
          const overflowY = style.overflowY;
          const overflowX = style.overflowX;
          if (
            overflowY === 'auto' ||
            overflowY === 'scroll' ||
            overflowX === 'auto' ||
            overflowX === 'scroll'
          ) {
            closestScrollableContainer = tempParent;
            break; // Use closest scrollable container
          }
          tempParent = tempParent.parentElement;
        }

        // Store container bounds for clipping
        if (closestScrollableContainer) {
          setContainerBounds(closestScrollableContainer.getBoundingClientRect());
        } else {
          setContainerBounds(null);
        }

        // Handle window resize and scroll to update highlight position
        const handlePositionUpdate = () => {
          if (foundElement) {
            const rect = foundElement.getBoundingClientRect();
            setTargetRect(rect);
            // Also update container bounds
            if (closestScrollableContainer) {
              setContainerBounds(closestScrollableContainer.getBoundingClientRect());
            }
          }
        };
        window.addEventListener('resize', handlePositionUpdate);
        window.addEventListener('scroll', handlePositionUpdate, true); // Use capture to catch all scroll events

        // Also listen for scroll on scrollable parent containers (for modal scrolling)
        // Find scrollable parent(s) and add scroll listeners
        const scrollableParents: Element[] = [];
        let parent = element.parentElement;
        while (parent) {
          const style = window.getComputedStyle(parent);
          const overflowY = style.overflowY;
          const overflowX = style.overflowX;
          if (
            overflowY === 'auto' ||
            overflowY === 'scroll' ||
            overflowX === 'auto' ||
            overflowX === 'scroll'
          ) {
            scrollableParents.push(parent);
            parent.addEventListener('scroll', handlePositionUpdate);
          }
          parent = parent.parentElement;
        }

        // Watch for element size/content changes using ResizeObserver
        let resizeObserver: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined') {
          resizeObserver = new ResizeObserver(() => {
            handlePositionUpdate();
          });
          resizeObserver.observe(element);

          // Also observe the header container for size changes
          // When header banners appear/disappear, the header height changes
          // which affects the position of all elements below it
          const headerElement = document.querySelector('[data-header-container]');
          if (headerElement) {
            resizeObserver.observe(headerElement);
          }
        }

        // Watch for DOM mutations that might affect element position (e.g., error messages)
        let mutationObserver: MutationObserver | null = null;
        if (typeof MutationObserver !== 'undefined') {
          mutationObserver = new MutationObserver(() => {
            // Delay slightly to let DOM settle after mutation
            requestAnimationFrame(handlePositionUpdate);
          });
          // Observe the parent container for child changes (error messages appearing)
          const parentEl = element.parentElement;
          if (parentEl) {
            mutationObserver.observe(parentEl, {
              childList: true,
              subtree: true,
              attributes: true,
            });
          }
        }

        // Watch for element removal (e.g., modal closes)
        watchIntervalId = setInterval(() => {
          if (foundElement && !document.body.contains(foundElement)) {
            clearInterval(watchIntervalId);
            window.removeEventListener('resize', handlePositionUpdate);
            window.removeEventListener('scroll', handlePositionUpdate, true);
            setTargetRect(null);
            setTargetElement(null);
            foundElement = null;

            // Auto-advance if element disappeared AND canAdvance returns true
            // Use refs to get the latest values to avoid stale closure issues
            const latestCanAdvance = canAdvanceRef.current;
            const latestStep = currentStepRef.current;
            const latestStepConfig = stepRef.current;
            if (
              latestCanAdvance &&
              latestCanAdvance(latestStep) &&
              !latestStepConfig?.waitForClick
            ) {
              advanceToNextStep();
            }
          }
        }, 100);

        // Store element reference for cleanup
        const elementForCleanup = element;

        cleanupFn = () => {
          // Remove click listener if it was added (note: { once: true } auto-removes after first click)
          if (handleTargetClick) {
            elementForCleanup?.removeEventListener('click', handleTargetClick);
          }
          window.removeEventListener('resize', handlePositionUpdate);
          window.removeEventListener('scroll', handlePositionUpdate, true);
          // Clean up scrollable parent listeners
          scrollableParents.forEach((scrollableParent) => {
            scrollableParent.removeEventListener('scroll', handlePositionUpdate);
          });
          resizeObserver?.disconnect();
          mutationObserver?.disconnect();
          clearInterval(watchIntervalId);
        };
      } else {
        attempts++;
        if (attempts >= maxAttempts) {
          clearInterval(intervalId);
          setTargetRect(null);
          setTargetElement(null);

          // If target not found and canAdvance returns false, keep waiting (don't reset)
          // This handles cases where we're waiting for an async operation to complete
          if (canAdvance && !canAdvance(currentStep)) {
            // Reset attempts and continue polling - the condition isn't met yet
            attempts = 0;
            intervalId = setInterval(findTarget, 100);
            return;
          }

          // If target not found and we're not on step 0, reset to step 0
          // This handles cases where user resumes tour but modal is closed
          if (currentStep > 0) {
            setCurrentStep(0);
            return;
          }

          // Auto-advance if target not found AND canAdvance returns true
          // This handles cases where a modal closes and the target disappears
          if (canAdvance && canAdvance(currentStep) && !step.waitForClick) {
            advanceToNextStep();
          }
        }
      }
    };

    // Start polling for element
    intervalId = setInterval(findTarget, 100);
    findTarget(); // Try immediately first

    return () => {
      clearInterval(intervalId);
      clearInterval(watchIntervalId);
      cleanupFn?.();
    };
  }, [currentStep, isActive, step]);

  const advanceToNextStep = () => {
    // Use refs to get the latest values to handle calls from stale closures (like setInterval callbacks)
    const latestStep = currentStepRef.current;
    if (latestStep < steps.length - 1) {
      const nextStep = latestStep + 1;
      // In controlled mode, only notify parent via onRequestStepChange
      // Parent is responsible for updating the step and can override the next step
      if (isControlled) {
        // Use ref to ensure we call the latest callback
        onRequestStepChangeRef.current?.(latestStep, nextStep);
        // Note: We don't call onStepChange here in controlled mode
        // because the parent may override toStep (e.g., skipping step 15)
        // onStepChange should be called by parent after updating controlledStep
      } else {
        // Call onStepChange callback before advancing - use ref for latest callback
        onStepChangeRef.current?.(latestStep, nextStep);
        setInternalStep(nextStep);
      }
    } else {
      handleComplete();
    }
  };

  const handleComplete = () => {
    setWasCompleted(true); // Prevent reactivation while Firebase updates
    setIsActive(false);
    onComplete?.();
  };

  const handleSkipRequest = () => {
    setShowSkipConfirm(true);
  };

  const handleSkipConfirm = () => {
    setWasCompleted(true); // Prevent reactivation while Firebase updates
    setIsActive(false);
    setShowSkipConfirm(false);
    onSkip?.();
  };

  const handleSkipCancel = () => {
    setShowSkipConfirm(false);
  };

  // Don't render anything for skipIfNotFound steps until we've confirmed the target exists
  // This prevents the brief flash of the tooltip before skipping
  if (!isActive || !step) return null;
  if (step.skipIfNotFound && !targetRect && !targetElement) return null;

  // Calculate tooltip position based on target element with responsive viewport handling
  const getTooltipStyle = (): React.CSSProperties => {
    const isMobile = window.innerWidth < 640; // sm breakpoint
    const isTablet = window.innerWidth >= 640 && window.innerWidth < 1024; // sm to lg
    const viewportPadding = isMobile ? 8 : 12; // Smaller padding on mobile
    // Compact tooltip: mobile ≤ 240px, tablet/laptop 240px, large desktop 260px
    const tooltipWidth = isMobile ? Math.min(window.innerWidth - 16, 240) : isTablet ? 240 : 260;
    const tooltipHeight = 150; // Approximate tooltip height for calculations
    // Cap tooltip height so it never overflows the viewport; content scrolls internally if taller
    const tooltipMaxHeight = `calc(100vh - ${viewportPadding * 2}px)`;
    const baseStyle: React.CSSProperties = {
      maxHeight: tooltipMaxHeight,
      overflowY: 'auto',
    };

    if (!targetRect) {
      return {
        ...baseStyle,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        maxWidth: `calc(100vw - ${viewportPadding * 2}px)`,
        width: tooltipWidth,
      };
    }

    const padding = viewportPadding;
    // Note: step.position is now a hint only — pickBestPlacement always chooses the side
    // with the most room so the tooltip never covers the highlighted target.

    // Calculate base position
    const style: React.CSSProperties = {
      ...baseStyle,
      maxWidth: `calc(100vw - ${viewportPadding * 2}px)`,
      width: tooltipWidth,
    };

    // Helper to clamp horizontal position within viewport
    const clampHorizontal = (leftPos: number): number => {
      const minLeft = viewportPadding;
      const maxLeft = window.innerWidth - tooltipWidth - viewportPadding;
      return Math.max(minLeft, Math.min(maxLeft, leftPos));
    };

    // Helper to clamp vertical position within viewport
    const clampVertical = (topPos: number): number => {
      const minTop = viewportPadding;
      const maxTop = window.innerHeight - tooltipHeight - viewportPadding;
      return Math.max(minTop, Math.min(maxTop, topPos));
    };

    // Pick the placement with the most available space from the four sides of the target,
    // so the tooltip never ends up covering the highlighted element.
    const pickBestPlacement = (): React.CSSProperties => {
      const spaceAbove = targetRect.top - padding - viewportPadding;
      const spaceBelow = window.innerHeight - targetRect.bottom - padding - viewportPadding;
      const spaceRight = window.innerWidth - targetRect.right - padding - viewportPadding;
      const spaceLeft = targetRect.left - padding - viewportPadding;
      const options = [
        { side: 'bottom' as const, space: spaceBelow, fitsDim: tooltipHeight },
        { side: 'top' as const, space: spaceAbove, fitsDim: tooltipHeight },
        { side: 'right' as const, space: spaceRight, fitsDim: tooltipWidth },
        { side: 'left' as const, space: spaceLeft, fitsDim: tooltipWidth },
      ];
      // Prefer a side where the tooltip actually fits; otherwise pick the one with most space
      const fitting = options.filter((o) => o.space >= o.fitsDim);
      const best = (fitting.length > 0 ? fitting : options).sort((a, b) => b.space - a.space)[0];

      const out: React.CSSProperties = {};
      if (best.side === 'bottom') {
        out.top = clampVertical(targetRect.bottom + padding);
        out.left = clampHorizontal(targetRect.left + targetRect.width / 2 - tooltipWidth / 2);
      } else if (best.side === 'top') {
        out.top = clampVertical(targetRect.top - padding - tooltipHeight);
        out.left = clampHorizontal(targetRect.left + targetRect.width / 2 - tooltipWidth / 2);
      } else if (best.side === 'right') {
        out.left = clampHorizontal(targetRect.right + padding);
        out.top = clampVertical(targetRect.top + targetRect.height / 2 - tooltipHeight / 2);
      } else {
        out.left = clampHorizontal(targetRect.left - padding - tooltipWidth);
        out.top = clampVertical(targetRect.top + targetRect.height / 2 - tooltipHeight / 2);
      }
      return out;
    };

    Object.assign(style, pickBestPlacement());
    return style;
  };

  // Create overlay with a "hole" for the target element to be clickable
  const renderOverlay = () => {
    // Skip overlay if noOverlay is set
    if (step.noOverlay) {
      return null;
    }

    if (!targetRect) {
      return <div className="fixed inset-0 bg-black/60 z-[9997]" />;
    }

    const padding = 8;

    // Calculate the highlight bounds
    let highlightTop = targetRect.top - padding;
    let highlightLeft = targetRect.left - padding;
    let highlightRight = targetRect.right + padding;
    let highlightBottom = targetRect.bottom + padding;

    // If there's a container bounds, clip the highlight to stay within it
    // This prevents the "hole" from exposing areas outside the scrollable container (like modal buttons)
    if (containerBounds) {
      highlightTop = Math.max(highlightTop, containerBounds.top);
      highlightLeft = Math.max(highlightLeft, containerBounds.left);
      highlightRight = Math.min(highlightRight, containerBounds.right);
      highlightBottom = Math.min(highlightBottom, containerBounds.bottom);
    }

    // Also clip to viewport bounds
    highlightTop = Math.max(0, highlightTop);
    highlightLeft = Math.max(0, highlightLeft);
    highlightRight = Math.min(window.innerWidth, highlightRight);
    highlightBottom = Math.min(window.innerHeight, highlightBottom);

    // Calculate final dimensions
    const top = highlightTop;
    const left = highlightLeft;
    const width = Math.max(0, highlightRight - highlightLeft);
    const height = Math.max(0, highlightBottom - highlightTop);

    // If the element is completely outside the container, show full overlay
    if (width <= 0 || height <= 0) {
      return <div className="fixed inset-0 bg-black/60 z-[9997]" />;
    }

    // Use slightly lower z-index when allowInteraction is true so dropdown portals appear on top
    // z-[9990] is still above modal content but below dropdown portals (usually z-[9999] or higher)
    const overlayZIndex = step.allowInteraction ? 'z-[9990]' : 'z-[9997]';

    return (
      <>
        {/* Top overlay */}
        <div
          className={`fixed bg-black/60 ${overlayZIndex}`}
          style={{
            top: 0,
            left: 0,
            right: 0,
            height: top,
          }}
        />
        {/* Left overlay */}
        <div
          className={`fixed bg-black/60 ${overlayZIndex}`}
          style={{
            top: top,
            left: 0,
            width: left,
            height: height,
          }}
        />
        {/* Right overlay */}
        <div
          className={`fixed bg-black/60 ${overlayZIndex}`}
          style={{
            top: top,
            left: left + width,
            right: 0,
            height: height,
          }}
        />
        {/* Bottom overlay */}
        <div
          className={`fixed bg-black/60 ${overlayZIndex}`}
          style={{
            top: top + height,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        />
      </>
    );
  };

  // Spotlight around the target
  const renderSpotlight = () => {
    if (!targetRect) return null;

    const padding = 8;

    // Calculate spotlight bounds (same clipping logic as overlay)
    let spotlightTop = targetRect.top - padding;
    let spotlightLeft = targetRect.left - padding;
    let spotlightRight = targetRect.right + padding;
    let spotlightBottom = targetRect.bottom + padding;

    // Clip to container bounds if available
    if (containerBounds) {
      spotlightTop = Math.max(spotlightTop, containerBounds.top);
      spotlightLeft = Math.max(spotlightLeft, containerBounds.left);
      spotlightRight = Math.min(spotlightRight, containerBounds.right);
      spotlightBottom = Math.min(spotlightBottom, containerBounds.bottom);
    }

    // Clip to viewport
    spotlightTop = Math.max(0, spotlightTop);
    spotlightLeft = Math.max(0, spotlightLeft);
    spotlightRight = Math.min(window.innerWidth, spotlightRight);
    spotlightBottom = Math.min(window.innerHeight, spotlightBottom);

    const width = Math.max(0, spotlightRight - spotlightLeft);
    const height = Math.max(0, spotlightBottom - spotlightTop);

    // Don't render spotlight if element is completely outside container
    if (width <= 0 || height <= 0) return null;

    // Use slightly lower z-index when allowInteraction is true so dropdown portals appear on top
    const spotlightZIndex = step.allowInteraction ? 'z-[9991]' : 'z-[9998]';

    return (
      <div
        className={`fixed rounded-lg ring-4 ring-yellow-400 ring-opacity-75 ${spotlightZIndex} animate-pulse pointer-events-none`}
        style={{
          top: spotlightTop,
          left: spotlightLeft,
          width: width,
          height: height,
        }}
      />
    );
  };

  return createPortal(
    <>
      {/* Skip confirmation dialog */}
      {showSkipConfirm && (
        <div className="fixed inset-0 bg-black/70 z-[10002] flex items-center justify-center p-3 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-[calc(100vw-24px)] sm:max-w-sm w-full p-4 sm:p-6">
            <h3 className="font-bold text-gray-900 text-base sm:text-lg mb-1.5 sm:mb-2">
              Skip Guide?
            </h3>
            <p className="text-gray-600 text-xs sm:text-sm mb-4 sm:mb-6">
              Are you sure you want to skip the guide?
            </p>
            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 sm:justify-end">
              <button
                onClick={handleSkipCancel}
                className="px-3 sm:px-4 py-2 text-gray-600 hover:text-gray-800 text-xs sm:text-sm font-medium text-center"
              >
                Continue Guide
              </button>
              <button
                onClick={handleSkipConfirm}
                className="px-3 sm:px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-xs sm:text-sm font-medium"
              >
                Skip Guide
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Overlay with hole for target element */}
      {renderOverlay()}

      {/* Spotlight on target */}
      {renderSpotlight()}

      {/* Tooltip */}
      <div
        className="fixed z-[10000] max-w-[calc(100vw-16px)] sm:max-w-[240px] lg:max-w-[260px] w-full bg-white rounded-lg shadow-2xl overflow-hidden"
        style={getTooltipStyle()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-yellow-400 to-orange-400 px-2 py-1.5 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <Rocket className="w-3 h-3 text-black flex-shrink-0" />
            <span className="font-semibold text-black text-[11px]">
              Step {currentStep + 1} of {steps.length}
            </span>
          </div>
          <button
            onClick={handleSkipRequest}
            className="p-0.5 hover:bg-black/10 rounded-full transition-colors flex-shrink-0"
          >
            <X className="w-3 h-3 text-black" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-0.5 bg-gray-200">
          <div
            className="h-full bg-yellow-500 transition-all duration-300"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>

        {/* Content */}
        <div className="p-2.5">
          <h3 className="font-bold text-gray-900 mb-1 text-xs sm:text-sm">{step.title}</h3>
          <p className="text-gray-600 text-[11px] sm:text-xs mb-2.5 leading-snug">{step.content}</p>

          {/* Action indicator */}
          {step.waitForClick ? (
            <div className="flex items-center justify-center gap-1.5 bg-yellow-50 border border-yellow-200 rounded-md p-1.5">
              <MousePointer className="w-3 h-3 text-yellow-600 animate-bounce flex-shrink-0" />
              <span className="text-[11px] font-medium text-yellow-800 text-center">
                {step.clickText || 'Click the highlighted element to continue'}
              </span>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-2">
              <button
                onClick={handleSkipRequest}
                className="text-gray-400 hover:text-gray-600 text-[10px] whitespace-nowrap"
              >
                Skip tour
              </button>
              <button
                onClick={advanceToNextStep}
                disabled={canAdvance ? !canAdvance(currentStep) : false}
                className={`font-medium px-3 py-1 rounded-md text-[11px] whitespace-nowrap ${
                  canAdvance && !canAdvance(currentStep)
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-yellow-400 hover:bg-yellow-500 text-black'
                }`}
              >
                {currentStep === steps.length - 1 ? 'Done' : 'Next'}
              </button>
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  );
};
